import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, Clock, XCircle, FileText, Landmark } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

export function HostVerificationsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState({});
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/host-verifications").then((r) => setData(r.data)).catch(() => setData({ stats: {}, requests: [], required_docs: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (userId, approve) => {
    setBusy(userId);
    try {
      await api.post(`/admin/host-verifications/${userId}/decision`, { approve, notes: notes[userId] || "" });
      toast.success(approve ? "Host approved — Verified Host badge is live." : "Verification rejected — host was notified.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (!data) return <p className="text-slate-400 text-sm">Loading…</p>;
  const { stats, requests, required_docs } = data;

  return (
    <div data-testid="host-verifications-panel" className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        {[["Pending review", stats.pending ?? 0, Clock, "text-amber-600 bg-amber-50"],
          ["Verified hosts", stats.approved ?? 0, BadgeCheck, "text-green-600 bg-green-50"],
          ["Rejected", stats.rejected ?? 0, XCircle, "text-red-500 bg-red-50"]].map(([label, value, Icon, tint]) => (
          <div key={label} className="bg-white rounded-2xl shadow-card border border-slate-100 p-5 flex items-center gap-4">
            <span className={`grid place-items-center w-10 h-10 rounded-xl ${tint}`}><Icon size={19} /></span>
            <div>
              <p className="font-heading text-2xl font-bold text-slate-900">{value}</p>
              <p className="text-slate-400 text-xs">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {requests.length === 0 && <p className="text-slate-400 text-sm bg-white rounded-2xl shadow-card p-8 text-center">No host verification requests yet. Hosts submit documents from their Host Portal → Settings.</p>}

      {requests.map((r) => (
        <div key={r.user_id} data-testid={`host-verification-${r.user_id}`} className="bg-white rounded-2xl shadow-card border border-slate-100 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="font-semibold text-slate-900 flex items-center gap-2">
                {r.name}
                {r.host_verified && <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 text-[10px] font-bold px-2 py-0.5 rounded-full"><BadgeCheck size={11} /> VERIFIED HOST</span>}
              </p>
              <p className="text-slate-400 text-xs mt-0.5">{r.email}{r.phone ? ` · ${r.phone}` : ""} · {r.car_count} vehicle{r.car_count === 1 ? "" : "s"}</p>
              <p className="text-slate-400 text-xs mt-0.5">Submitted {(r.submitted_at || "").slice(0, 10)}{r.reviewed_at ? ` · Reviewed ${String(r.reviewed_at).slice(0, 10)}` : ""}</p>
            </div>
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full capitalize ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-500"}`}>{r.status}</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
            {required_docs.map((d) => {
              const url = (r.documents || {})[d.key];
              return (
                <div key={d.key}>
                  {url ? (
                    <button onClick={() => setPreview({ url, label: d.label })} data-testid={`doc-${r.user_id}-${d.key}`} className="w-full">
                      <img src={url} alt={d.label} className="w-full aspect-[4/3] object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-blue-400" />
                    </button>
                  ) : (
                    <div className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-slate-200 grid place-items-center text-slate-300"><FileText size={18} /></div>
                  )}
                  <p className="text-[10px] text-slate-500 mt-1 text-center">{d.label}</p>
                </div>
              );
            })}
          </div>
          <p className={`mt-3 text-xs font-semibold flex items-center gap-1.5 ${r.bank_verified ? "text-green-600" : "text-amber-600"}`}>
            <Landmark size={13} /> {r.bank_verified ? "Payout bank account on file" : "No payout bank account on file yet"}
          </p>

          {r.status === "pending" && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3 flex-wrap">
              <input value={notes[r.user_id] || ""} onChange={(e) => setNotes((p) => ({ ...p, [r.user_id]: e.target.value }))}
                placeholder="Reviewer note (optional, sent on rejection)" data-testid={`hv-note-${r.user_id}`}
                className="belo-input flex-1 min-w-[220px] text-sm" />
              <button onClick={() => decide(r.user_id, true)} disabled={busy === r.user_id} data-testid={`hv-approve-${r.user_id}`}
                className="bg-green-600 text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-green-700 disabled:opacity-50">Approve</button>
              <button onClick={() => decide(r.user_id, false)} disabled={busy === r.user_id} data-testid={`hv-reject-${r.user_id}`}
                className="border-2 border-red-200 text-red-600 px-5 py-2 rounded-full text-sm font-semibold hover:bg-red-50 disabled:opacity-50">Reject</button>
            </div>
          )}
          {r.notes && <p className="mt-3 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">Note: {r.notes}</p>}
        </div>
      ))}

      {preview && (
        <div className="fixed inset-0 z-[70] bg-slate-900/80 grid place-items-center p-6" onClick={() => setPreview(null)}>
          <div className="max-w-3xl w-full">
            <img src={preview.url} alt={preview.label} className="w-full max-h-[75vh] object-contain rounded-xl" />
            <p className="text-white text-sm text-center mt-3 font-semibold">{preview.label}</p>
          </div>
        </div>
      )}
    </div>
  );
}
